import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useFetch from "./ApiClient/GetApi";
import { PostApi } from "./ApiClient/PostApi"; // ✅ use one import
import "./Articles.css";
import LunaticBanner from "./LunaticBanner";
import { formatDate } from "./Utils/DateFormat";
import VideoPlayer from "./VideoPlayer/VideoPlayer";
import CategoryTwo from "./CategoryTwo";

export default function Articles() {
  const [loading, setLoading] = useState(true);

  // Infinite feed state
  const [moreNews, setMoreNews] = useState([]);
  const [page, setPage] = useState(1); // if your API is 0-based, set to 0
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loaderRef = useRef(null);
  const observerRef = useRef(null);

  const { article } = useParams();

  // Current article details
  const { apiData } = useFetch(`/user/newsDetails/${article}`);
  console.log(apiData)
  const currentArticle = apiData?.data?.newsData;
  console.log(currentArticle)



  // Initial skeleton
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(t);
  }, []);

  // --- Helpers (set root if you scroll inside a container instead of window) ---
  const getRootEl = () => null; // e.g. document.querySelector('.home-container')
  const isInView = (el, rootEl = null, extra = 0) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    let viewTop = 0,
      viewBottom = window.innerHeight;
    if (rootEl) {
      const r = rootEl.getBoundingClientRect();
      viewTop = r.top;
      viewBottom = r.bottom;
    }
    return rect.top < viewBottom + extra && rect.bottom > viewTop - extra;
  };

  // ---- Fetch more items (POST) ----
  const fetchMore = useCallback(async () => {
    if (!currentArticle?.id) return;       // wait until the article is known
    if (loadingMore || !hasMore) return;   // guard rails

    setLoadingMore(true);
    try {
      const payload = {
        page,
        limit: 5,
        excludeId: currentArticle.id,
      };

      const res = await PostApi("/user/getMoreNews", payload);
      // Expected shape: { status, message, data: { items: [...], hasMore: boolean, pagination: {...} } }
      const ok = res?.data?.status === 1;
      const data = res?.data?.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      const nextHasMore = Boolean(data.hasMore);

      setMoreNews((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        const incoming = items.filter(
          (n) => n?.id && !seen.has(n.id) && n.id !== currentArticle.id
        );
        return incoming.length ? [...prev, ...incoming] : prev;
      });

      setHasMore(nextHasMore);
      if (ok && nextHasMore) setPage((p) => p + 1);
    } catch (err) {
      console.error("fetchMore error:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore, currentArticle?.id]);

  // ---- Rebuild IntersectionObserver AFTER the article is ready ----
  useEffect(() => {
    if (!currentArticle?.id || !loaderRef.current) return;

    // Disconnect old observer
    if (observerRef.current) observerRef.current.disconnect();

    const rootEl = getRootEl(); // set this if you scroll inside a container

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loadingMore) {
          fetchMore();
        }
      },
      {
        root: rootEl || null,
        threshold: 0.01,           // fire when just visible
        rootMargin: "800px 0px",   // prefetch earlier
      }
    );

    observerRef.current.observe(loaderRef.current);
    return () => observerRef.current?.disconnect();
  }, [currentArticle?.id, hasMore, loadingMore, fetchMore]);

  // ---- Kickstart once the article arrives (if sentinel already visible or page too short) ----
  useEffect(() => {
    if (!currentArticle?.id || !hasMore || loadingMore) return;

    const rootEl = getRootEl();
    const el = loaderRef.current;

    const docShort = rootEl
      ? rootEl.scrollHeight <= rootEl.clientHeight + 10
      : document.body.scrollHeight <= window.innerHeight + 10;

    if (docShort || isInView(el, rootEl, 900)) {
      fetchMore();
    }
  }, [currentArticle?.id]); // run once when article becomes known

  // ---- (Optional) Fallback scroll listener ----
  useEffect(() => {
    if (!currentArticle?.id || !hasMore) return;
    const rootEl = getRootEl();
    const target = rootEl || window;

    const onScroll = () => {
      if (loadingMore) return;
      if (isInView(loaderRef.current, rootEl, 900)) {
        fetchMore();
      }
    };

    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [currentArticle?.id, hasMore, loadingMore, fetchMore]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="spinner"
          style={{
            width: 48,
            height: 48,
            border: "5px solid #c20a7c",
            borderTop: "5px solid #fff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Small renderer for a news block (used for appended items)
  const NewsBlock = ({ item }) => {
    const catName = item?.category?.name ?? "General";
    const catId = item?.category?.id ?? "";
    return (
      <>
        {/* Section: Hero/Main Feature for each appended news */}
        <div className="articles-hero-row">
          <div className="articles-hero-card">
            <div className="articles-hero-main">
              <Link to={`/category/${catId}`} style={{ textDecoration: "none" }}>
                <div className="articles-hero-tag">{catName}</div>
              </Link>
              <h1 className="articles-hero-title">{item?.title ?? ""}</h1>
            </div>
            <div className="articles-hero-meta">
              * {item?.user?.name ?? ""}
              <br />
              {formatDate(item?.createdAt)}
            </div>
          </div>
          <div className="articles-hero-img">
            <img
              src={item?.cover || item?.thumbnail || "/placeholder.jpg"}
              alt={item?.title || "cover"}
              style={{
                borderRadius: "18px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
              }}
            />
          </div>
        </div>

       
        <div className="blog-detail-row" style={{ display: "flex", gap: "2.2rem" }}>
          {/* Left: Main Content */}
          <div
            className="blog-detail-main"
            style={{ flex: 1 }}
            dangerouslySetInnerHTML={{
              __html: item?.content ?? "",
            }}
          />
          {/* Right: Sidebar */}
          <div className="blog-detail-sidebar" style={{ flex: 0.5 }}>
            <div className="newsletter-box">
              <h3 className="newsletter-title">
                YOUR DAILY FIX ON ALL THINGS IN & AROUND DUBAI!!
              </h3>
              <p className="subscribe-text">
                Subscribe for free to That Dubai Page Newsletter
              </p>
              <input className="newsletter-input" placeholder="your email here" />
              <p className="newsletter-privacy">
                By subscribing to this BDG newsletter, you are aggree to our{" "}
                <a className="privacy-sub">Terms of Service</a> and{" "}
                <a className="privacy-sub">Privacy Policy</a>
              </p>
              <button className="newsletter-btn">SUBMIT</button>
            </div>

            <div className="sidebar-banner-img">
              <img src="/40.jpg" alt="sidebar-banner" />
            </div>

            <div className="latest-greatest-box">
              <div className="latest-greatest-title">
                <span className="latest-arrow-left">
                  <FontAwesomeIcon icon={faArrowLeft} className="latest-arrow" />
                </span>{" "}
                LATEST & GREATEST
              </div>

              <div
                className="latest-greatest-list"
                style={{ display: "flex", gap: "0.5rem" }}
              >
                {apiData?.data?.relatedNews?.map((item, idx) => (
                  <div
                    className="latest-greatest-item-card"
                    key={item?.id || item?.slug || idx}
                    style={{ borderRadius: "14px", overflow: "hidden" }}
                  >
                    <img
                      src={item?.thumbnail || "/placeholder.jpg"}
                      alt={item?.title}
                      className="latest-greatest-img"
                      style={{ borderRadius: "12px" }}
                    />
                    <div className="latest-greatest-item-content">
                      <Link
                        to={`/category/${item?.category?.id || ""}`}
                        style={{ textDecoration: "none" }}
                      >
                        <span
                          className={`tag ${item?.category?.name?.toLowerCase() || ""
                            }`}
                        >
                          {item?.category?.name || "General"}
                        </span>
                      </Link>
                      <Link
                        to={`/article/${item?.slug || item?.title}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <div className="latest-greatest-headline">
                          {item?.title?.split(" ").slice(0, 20).join(" ")}
                        </div>

                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <LunaticBanner />
      </>
    );
  };

  return (
    <div className="home-container">
      {
        currentArticle?.template == "template1" ? <div> <main>
          <VideoPlayer url="https://www.youtube.com/watch?v=3VpFFKGCaLQ&list=RD3VpFFKGCaLQ&start_radio=1" />
          {/* --- Current Article (single, no loop) --- */}
          {currentArticle && (
            <>
              {/* Section 1: Hero/Main Feature */}
              <div className="articles-hero-row">
                <div className="articles-hero-card">
                  <div className="articles-hero-main">
                    <Link to="/category" style={{ textDecoration: "none" }}>
                      <div className="articles-hero-tag">
                        {currentArticle?.category?.name ?? ""}
                      </div>
                    </Link>
                    <h1 className="articles-hero-title">
                      {currentArticle?.title ?? ""}
                    </h1>
                    <div className="articles-hero-sub">
                      Get ready for a world of wonder, rides, and unforgettable
                      experiences on Yas Island.
                    </div>
                  </div>
                  <div className="articles-hero-meta">
                    * {currentArticle?.user?.name ?? ""}
                    <br />
                    {formatDate(currentArticle?.createdAt)}
                  </div>
                </div>
                <div className="articles-hero-img">
                  <img
                    // src="/25.jpg"
                    src={currentArticle.thumbnail}
                    alt="Disney Abu Dhabi"
                    style={{
                      borderRadius: "18px",
                      boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                    }}
                  />
                </div>
              </div>

              {/* Section 2: Blog Detail Row with Sticky Sidebar */}
              <div className="blog-detail-row" style={{ display: "flex", gap: "2.2rem" }}>
                {/* Left: Main Content */}
                <div
                  className="blog-detail-main"
                  style={{ flex: 1 }}
                  dangerouslySetInnerHTML={{
                    __html: currentArticle?.content ?? "",
                  }}
                />
                {/* Right: Sidebar */}
                <div className="blog-detail-sidebar" style={{ flex: 0.5 }}>
                  <div className="newsletter-box">
                    <h3 className="newsletter-title">
                      YOUR DAILY FIX ON ALL THINGS IN & AROUND DUBAI!!
                    </h3>
                    <p className="subscribe-text">
                      Subscribe for free to That Dubai Page Newsletter
                    </p>
                    <input className="newsletter-input" placeholder="your email here" />
                    <p className="newsletter-privacy">
                      By subscribing to this BDG newsletter, you are aggree to our{" "}
                      <a className="privacy-sub">Terms of Service</a> and{" "}
                      <a className="privacy-sub">Privacy Policy</a>
                    </p>
                    <button className="newsletter-btn">SUBMIT</button>
                  </div>

                  {
                    apiData?.data?.istAd && <a href={`https://${apiData?.data?.istAd?.link}`} target="_blank">
                      <div className="sidebar-banner-img">
                        <img src={apiData?.data?.istAd?.image} alt="sidebar-banner" />
                      </div>
                    </a>
                  }


                  <div className="latest-greatest-box">
                    <div className="latest-greatest-title">
                      <span className="latest-arrow-left">
                        <FontAwesomeIcon icon={faArrowLeft} className="latest-arrow" />
                      </span>{" "}
                      LATEST & GREATEST
                    </div>

                    <div
                      className="latest-greatest-list"
                      style={{ display: "flex", gap: "0.5rem" }}
                    >
                      {apiData?.data?.relatedNews?.map((item, idx) => (
                        <div
                          className="latest-greatest-item-card"
                          key={item?.id || item?.slug || idx}
                          style={{ borderRadius: "14px", overflow: "hidden" }}
                        >
                          <img
                            src={item?.thumbnail || "/placeholder.jpg"}
                            alt={item?.title}
                            className="latest-greatest-img"
                            style={{ borderRadius: "12px" }}
                          />
                          <div className="latest-greatest-item-content">
                            <Link
                              to={`/category/${item?.category?.id || ""}`}
                              style={{ textDecoration: "none" }}
                            >
                              <span
                                className={`tag ${item?.category?.name?.toLowerCase() || ""
                                  }`}
                              >
                                {item?.category?.name || "General"}
                              </span>
                            </Link>
                            <Link
                              to={`/article/${item?.slug}`}
                              style={{ textDecoration: "none", color: "inherit" }}
                            >
                              <div className="latest-greatest-headline">
                                {item?.title}
                              </div>
                            </Link>

                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <LunaticBanner />
            </>
          )}

          {/* --- Appended feed items (loaded via POST) --- */}
          {moreNews.map((n) => (
            <NewsBlock key={n?.id || n?.slug || n?.title} item={n} />
          ))}

          {/* Sentinel / Loader */}
          <div
            ref={loaderRef}
            style={{ textAlign: "center", margin: "2rem 0", minHeight: 60 }}
          >
            {loadingMore && (
              <div
                className="spinner"
                style={{
                  width: 38,
                  height: 38,
                  border: "4px solid #c20a7c",
                  borderTop: "4px solid #fff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto",
                }}
              />
            )}
            {!hasMore && (
              <div style={{ opacity: 0.6, fontSize: 14, padding: 8 }}>
                You’re all caught up.
              </div>
            )}
          </div>
        </main></div> : <div>
          <CategoryTwo slug={article} />
        </div>
      }
    </div>
  );
}
